/**
 * Horizontal Marquee with API Data
 * Fetches avis data from API and renders it in a horizontal marquee on desktop
 * and as a swiper on mobile
 */
class HorizontalMarquee extends HTMLElement {
  constructor() {
    super();
    this.speed = parseFloat(this.dataset.scrollingSpeed) || 0.2;
    this.direction = this.dataset.direction || 'right';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.itemsToShow = parseInt(this.dataset.itemsToShow) || 24;
    this.i = 0;
    this.isPaused = false;
    this.isStopped = false;
    this.isLoading = true;
    this.abortController = null;
    this.swiper = null;
    this.isMobile = window.innerWidth < 750;

    // Bind methods
    this._animate = this._animate.bind(this);
    this._pause = this._pause.bind(this);
    this._resume = this._resume.bind(this);
    this._onResize = this._onResize.bind(this);
    this.loadAvisData = this.loadAvisData.bind(this);
    this.renderAvisCards = this.renderAvisCards.bind(this);
    this.initializeSwiper = this.initializeSwiper.bind(this);
    this.destroySwiper = this.destroySwiper.bind(this);

    // Set up event listeners
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', this._pause);
      this.addEventListener('mouseleave', this._resume);
    }

    window.addEventListener('resize', this._onResize);
  }

  connectedCallback() {
    // Create a loading indicator
    this.innerHTML = `<div class="tw-flex tw-justify-center tw-items-center tw-py-8 tw-w-full">
      <svg class="spinner tw-h-8 tw-w-8" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;

    // Load data from API
    this.loadAvisData();
  }

  loadAvisData() {
    this.isLoading = true;

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    // console.log(`Fetching avis data from API for marquee...`);
    let developmentModeUrl = window?.developmentMode?.url ? `${window?.developmentMode?.url}/api/shopify/avis/list?page=1&limit=${this.itemsToShow}` : `https://yves-legare-server.vercel.app/api/shopify/avis/list?page=1&limit=${this.itemsToShow}`;
    let developmentModeEnabled = window?.developmentMode?.enabled;
    let url = developmentModeEnabled ? developmentModeUrl : `https://yves-legare-server.vercel.app/api/shopify/avis/list?page=1&limit=${this.itemsToShow}`;
    let targetStoreHeader = window?.targetStore?.enabled ? window?.targetStore?.value : null;
    let fetchOptions = {
      signal: this.abortController.signal
    };

    if (targetStoreHeader) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        'x-target-store': targetStoreHeader
      };
    }

    fetch(url, {
      ...fetchOptions
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        // console.log('Avis data received for marquee:', data);

        if (!data?.results || !Array.isArray(data.results) || data.results.length === 0) {
          console.error('Invalid or empty data received for marquee');
          return;
        }

        this.avisItems = data.results;
        this.renderAvisCards();
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Error fetching avis data for marquee:', error);
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  renderAvisCards() {
    // Clear the loading indicator
    this.innerHTML = '';

    if (this.isMobile) {
      // Create swiper structure for mobile
      this.innerHTML = `
        <style>
          .marquee-swiper {
            width: 100%;
            overflow: hidden;
            position: relative;
            padding: 0 24px;
          }
          .marquee-swiper .swiper-slide {
            height: auto;
            flex-shrink: 0;
          }
          .marquee-swiper .avis-marquee-card {
            width: 100%;
            transition: transform 0.3s ease;
            position: relative;
            display: block;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .marquee-swiper .swiper-pagination {
            position: relative;
            margin-top: 16px;
          }
          .marquee-swiper .avis-marquee-card__content {
            display: block;
            text-decoration: none;
            color: inherit;
          }
          .marquee-swiper .avis-marquee-card__content-image-wrapper {
            width: 100%;
            position: relative;
            overflow: hidden;
            margin-bottom: 12px;
            border-radius: 8px;
          }
          .marquee-swiper .avis-marquee-card__img,
          .marquee-swiper .avis-marquee-card__img-placeholder img {
            width: 100%;
            aspect-ratio: 1 / 1;
            object-fit: cover;
            display: block;
          }
          .marquee-swiper .avis-marquee-card__content-body {
            padding: 0;
          }
          .marquee-swiper .avis-marquee-card__content-body h5 {
            margin-top: 0;
            margin-bottom: 4px;
            font-size: 16px;
            line-height: 1.2;
          }
          .marquee-swiper .avis-marquee-card__content-body h5 span {
            margin-right: 4px;
          }
          .marquee-swiper .avis__quick-view {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .marquee-swiper .avis-marquee-card:hover .avis__quick-view {
            opacity: 1;
          }
          .swiper-pagination-bullet {
            background: var(--color-base-accent-1, #000);
            opacity: 0.5;
          }
          .swiper-pagination-bullet-active {
            opacity: 1;
          }
        </style>
        <div class="swiper marquee-swiper">
          <div class="swiper-wrapper tw-w-full"></div>
          <div class="swiper-pagination"></div>
        </div>
      `;

      const swiperWrapper = this.querySelector('.swiper-wrapper');

      // Create the content for the swiper
      this.avisItems.forEach(avis => {

        const firstName = avis.firstName || avis.prenom || '';
        const lastName = avis.lastName || avis.nom_de_famille || '';
        const MaidenName = avis.MaidenName || '';
        const birthDate = avis.birthDate || avis.date_de_naissance || '';
        const deathDate = avis.deathDate || avis.date_de_deces || '';

        const birthYear = birthDate ? birthDate.split('-')[0] : '';
        const deathYear = deathDate ? deathDate.split('-')[0] : '';

        const matchingAvis = window.avis && window.avis.find(
          (item) => item.unique_id === avis.contractId
        );

        const imageUrl = !avis.error ? avis.image?.url : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
        // const imageUrl = matchingAvis ? matchingAvis.avis_image : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
        const avisUrl = matchingAvis ? matchingAvis.avis_url : `/pages/avis-de-deces/${avis.handle || ''}`;
        const placeholderImage = window.themeVariables.avisImagePlaceholder ?? window.avisSvg;

        let fullName = "";

        if (MaidenName != '' && lastName != '') {
          fullName = `<span>${firstName} ${lastName} (née ${MaidenName})</span>`;
        } else if (lastName === '' && MaidenName != '') {
          fullName = `<span>${firstName} ${MaidenName}</span>`;
        } else if (lastName != '' && firstName != '') {
          fullName = `<span>${firstName} ${lastName}</span>`;
        }

        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = `
          <div class="avis-marquee-card">
            <a href="${avisUrl}" class="avis-marquee-card__content">
              <div class="avis-marquee-card__content-image-wrapper tw-relative">
                ${imageUrl ?
            `<img
                    src="${imageUrl}"
                    alt="${fullName}"
                    width="304"
                    height="296"
                    class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                    loading="lazy"
                  >` :
            `<div class="avis-marquee-card__img-placeholder">
                    <img
                      src="${placeholderImage}"
                      alt=""
                      width="304"
                      height="296"
                      class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                      loading="lazy"
                    >
                  </div>`
          }
                <div class="avis__quick-view tw-absolute tw-bottom-4 tw-left-4 tw-z-10">
                  <svg class="avis-marquee-card__icon-eye" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5.25C4.5 5.25 1.5 12 1.5 12C1.5 12 4.5 18.75 12 18.75C19.5 18.75 22.5 12 22.5 12C22.5 12 19.5 5.25 12 5.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="avis-marquee-card__content-body tw-flex tw-flex-col">
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                  <h5 class="tw-font-bold tw-mt-4 tw-text-left tw-capitalize tw-font-heading">
                    ${fullName}
                  </h5>
                </div>
                <div class="tw-flex tw-gap-1">
                  ${birthYear ? `<p>${birthYear}</p>` : ''}
                  ${birthYear && deathYear ? `<span>-</span>` : ''}
                  ${deathYear ? `<p>${deathYear}</p>` : ''}
                </div>
              </div>
            </a>
          </div>
        `;

        swiperWrapper.appendChild(slide);
      });

      // Initialize Swiper
      this.initializeSwiper();
    } else {
      // Create the content for the marquee (desktop)
      const avisCardsHTML = this.avisItems.map(avis => {
        const firstName = avis.firstName || avis.prenom || '';
        const lastName = avis.lastName || avis.nom_de_famille || '';
        const MaidenName = avis.MaidenName || '';
        const birthDate = avis.birthDate || avis.date_de_naissance || '';
        const deathDate = avis.deathDate || avis.date_de_deces || '';

        const birthYear = birthDate ? birthDate.split('-')[0] : '';
        const deathYear = deathDate ? deathDate.split('-')[0] : '';

        const matchingAvis = window.avis && window.avis.find(
          (item) => item.unique_id === avis.contractId
        );

        const imageUrl = !avis.error ? avis.image?.url : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
        // const imageUrl = matchingAvis ? matchingAvis.avis_image : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
        const avisUrl = matchingAvis ? matchingAvis.avis_url : `/pages/avis-de-deces/${avis.handle || ''}`;
        const placeholderImage = window.themeVariables.avisImagePlaceholder ?? window.avisSvg;

        let fullName = "";

        if (MaidenName != '' && lastName != '') {
          fullName = `<span>${firstName} ${lastName} (née ${MaidenName})</span>`;
        } else if (lastName === '' && MaidenName != '') {
          fullName = `<span>${firstName} ${MaidenName}</span>`;
        } else if (lastName != '' && firstName != '') {
          fullName = `<span>${firstName} ${lastName}</span>`;
        }

        return `
          <div class="avis-marquee-card">
            <a href="${avisUrl}" class="avis-marquee-card__content">
              <div class="avis-marquee-card__content-image-wrapper tw-relative">
                ${imageUrl ?
            `<img
                    src="${imageUrl}"
                    alt="${fullName}"
                    width="304"
                    height="296"
                    class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                    loading="lazy"
                  >` :
            `<div class="avis-marquee-card__img-placeholder">
                    <img
                      src="${placeholderImage}"
                      alt=""
                      width="304"
                      height="296"
                      class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                      loading="lazy"
                    >
                  </div>`
          }
                <div class="avis__quick-view tw-absolute tw-bottom-4 tw-left-4 tw-z-10">
                  <svg class="avis-marquee-card__icon-eye" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5.25C4.5 5.25 1.5 12 1.5 12C1.5 12 4.5 18.75 12 18.75C19.5 18.75 22.5 12 22.5 12C22.5 12 19.5 5.25 12 5.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="avis-marquee-card__content-body tw-flex tw-flex-col">
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                  <h5 class="tw-font-bold tw-mt-4 tw-text-left tw-capitalize tw-font-heading">
                    ${firstName ? `<span>${firstName}</span>` : ''}
                    ${lastName ? `<span>${lastName}</span>` : ''}
                  </h5>
                </div>
                <div class="tw-flex tw-gap-1">
                  ${birthYear ? `<p>${birthYear}</p>` : ''}
                  ${birthYear && deathYear ? `<span>-</span>` : ''}
                  ${deathYear ? `<p>${deathYear}</p>` : ''}
                </div>
              </div>
            </a>
          </div>
        `;
      }).join('');

      // Set the content
      this.innerHTML = avisCardsHTML;

      // Clone the content for the marquee effect
      this.clone = this.innerHTML;
      this.insertAdjacentHTML('beforeend', this.clone);
      this.insertAdjacentHTML('beforeend', this.clone);

      // Get the first element for animation
      this.firstElement = this.children[0];

      // Start the animation
      this._animate();
    }
  }

  initializeSwiper() {
    if (!this.isMobile) return;

    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) {
      console.error('Swiper element not found');
      return;
    }

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded');
      return;
    }

    // Destroy existing swiper if it exists
    this.destroySwiper();

    // Initialize Swiper with a slight delay to ensure DOM is ready
    setTimeout(() => {
      try {
        this.swiper = new Swiper(swiperElement, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          centeredSlides: false,
          grabCursor: true,
          normalizeSlideIndex: false,
          cssMode: false,
          loop: true,
          autoplay: {
            delay: 3500,
            disableOnInteraction: false,
          },
          resizeObserver: true,
          breakpoints: {
            480: {
              slidesPerView: 1.3,
              spaceBetween: 16,
            },
            600: {
              slidesPerView: 2.4,
              spaceBetween: 16,
            }
          },
          on: {
            init: function () {
              // Force recalculation of slide sizes
              setTimeout(() => {
                if (!this || !this.params) return;

                this.update();

                // Manually set slide widths based on container width and slidesPerView
                const slides = this.slides;
                if (!slides || !slides.length) return;

                const containerWidth = this.width;
                const slidesPerView = this.params.slidesPerView;
                if (!containerWidth || !slidesPerView) return;

                const slideWidth = (containerWidth / slidesPerView) - (this.params.spaceBetween * (slidesPerView - 1) / slidesPerView);

                slides.forEach(slide => {
                  slide.style.width = `${slideWidth}px`;
                });
              }, 100);
            },
            resize: function () {
              // Update slide widths on resize
              setTimeout(() => {
                if (!this || !this.params) return;

                this.update();

                // Recalculate slide widths
                const slides = this.slides;
                if (!slides || !slides.length) return;

                const containerWidth = this.width;
                const slidesPerView = this.params.slidesPerView;
                if (!containerWidth || !slidesPerView) return;

                const slideWidth = (containerWidth / slidesPerView) - (this.params.spaceBetween * (slidesPerView - 1) / slidesPerView);

                slides.forEach(slide => {
                  slide.style.width = `${slideWidth}px`;
                });
              }, 100);
            }
          }
        });
        // console.log('Swiper initialized successfully:', this.swiper);
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }, 100);
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      //   console.log('Swiper destroyed');
    }
  }

  _checkInitialWidth() {
    this.isMobile = window.innerWidth < 750;
  }

  _animate() {
    if (!this.isPaused && !this.isMobile && this.firstElement) {
      const directionMultiplier = this.direction === 'right' ? 1 : -1;
      this.firstElement.style.marginLeft = `-${this.i}px`;
      this.i += this.speed * directionMultiplier;
    }
    requestAnimationFrame(this._animate);
  }

  _pause() {
    this.isPaused = true;
  }

  _resume() {
    this.isPaused = false;
  }

  _onResize() {
    const wasMobile = this.isMobile;
    this._checkInitialWidth();

    // If viewport changed between mobile and desktop
    if (wasMobile !== this.isMobile) {
      //   console.log(`Viewport changed from ${wasMobile ? 'mobile' : 'desktop'} to ${this.isMobile ? 'mobile' : 'desktop'}`);

      if (this.isMobile) {
        // Switching to mobile
        this.i = 0;
        if (this.firstElement) {
          this.firstElement.style.marginLeft = '0px';
        }
        this.renderAvisCards();
      } else {
        // Switching to desktop
        this.destroySwiper();
        this.renderAvisCards();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.pauseOnHover) {
      this.removeEventListener('mouseenter', this._pause);
      this.removeEventListener('mouseleave', this._resume);
    }
    this.destroySwiper();
  }
}

// Define the custom element
if (!window.customElements.get('horizontal-marquee')) {
  window.customElements.define('horizontal-marquee', HorizontalMarquee);
}